import type {ElcRisksAndControls} from "../../types";

export const ELC_RISKS_AND_CONTROLS: ElcRisksAndControls[] = [
  {
    elcId: "elc-control-environment",
    risks: [
      {
        id: "risk-ce-01",
        riskNumber: "CE-R01",
        title: "Inadequate tone at the top",
        description:
          "Management may not consistently reinforce ethical values and integrity expectations, leading to a control environment where employees do not prioritise accurate financial reporting or compliance with internal controls.",
        owner: "Lisa Wang",
        inherentScore: {
          components: {complexity: 3, subjectivity: 4, change: 3, uncertainty: 4, fraudSusceptibility: 4},
          rationale:
            "High subjectivity in assessing the effectiveness of tone-at-the-top messaging. High uncertainty as cultural factors are difficult to measure. Significant fraud susceptibility as weak ethical culture creates opportunity for management override.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-18"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 3, change: 2, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "Annual Code of Conduct attestation (CE-01) provides documented evidence of employee acknowledgement. Ethics training (CE-02) reinforces expectations. Residual subjectivity remains as the sincerity of commitment is inherently difficult to assess.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-18"),
        },
        status: "complete",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-ce-01",
            controlNumber: "CE-01",
            title: "Code of Conduct Annual Attestation",
            description:
              "All employees, contractors, and third-party agents are required to acknowledge the Code of Conduct upon hire and annually thereafter. Completion is tracked by the General Counsel's office with escalation for non-compliance.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2026-01-15"),
            status: "complete",
          },
          {
            id: "ctrl-ce-02",
            controlNumber: "CE-02",
            title: "Ethics Training Programme",
            description:
              "Mandatory annual ethics training covering conflicts of interest, anti-bribery, data protection, and financial reporting integrity. Supplemental modules for finance, procurement, and IT staff.",
            designScore: 3,
            implementationScore: 2,
            lastAssessed: new Date("2026-01-15"),
            status: "in-review",
          },
        ],
      },
      {
        id: "risk-ce-02",
        riskNumber: "CE-R02",
        title: "Insufficient board and audit committee oversight",
        description:
          "The board or audit committee may not receive timely, complete, or accurate information about the organisation's financial reporting risks, leading to inadequate governance oversight and delayed response to emerging issues.",
        owner: "Lisa Wang",
        inherentScore: {
          components: {complexity: 3, subjectivity: 4, change: 3, uncertainty: 4, fraudSusceptibility: 3},
          rationale:
            "High subjectivity in determining what constitutes adequate board reporting. High uncertainty as the quality of oversight is difficult to measure objectively. Moderate fraud susceptibility as weak governance creates opportunity for management override.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-20"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 3, change: 2, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "Quarterly board reporting (CE-03) ensures regular information flow, reducing complexity and change risk. The escalation protocol (CE-04) provides a safety net for urgent matters. Residual subjectivity remains as the completeness of reporting packages is subjective.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-20"),
        },
        status: "complete",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-ce-03",
            controlNumber: "CE-03",
            title: "Quarterly Board Reporting Package",
            description:
              "Management prepares and presents a comprehensive financial reporting risk summary to the audit committee at each quarterly meeting, including open deficiencies, remediation status, and emerging risks.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2026-01-15"),
            status: "complete",
          },
          {
            id: "ctrl-ce-04",
            controlNumber: "CE-04",
            title: "Critical Issue Escalation Protocol",
            description:
              "Critical financial reporting issues identified between scheduled meetings are escalated to the audit committee chair within 48 hours, with an ad hoc session convened if warranted.",
            designScore: 3,
            implementationScore: 2,
            lastAssessed: new Date("2026-01-15"),
            status: "in-review",
          },
        ],
      },
      {
        id: "risk-ce-03",
        riskNumber: "CE-R03",
        title: "Inadequate competence and accountability",
        description:
          "Individuals in key financial reporting roles may lack the necessary qualifications, experience, or training to fulfil their control responsibilities effectively, increasing the risk of errors or omissions in financial reporting.",
        owner: "James Park",
        inherentScore: {
          components: {complexity: 3, subjectivity: 3, change: 3, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "Moderate complexity from the range of technical competencies required across accounting, tax, and IT. Moderate change risk as evolving accounting standards (e.g., IFRS 17) require continuous upskilling. Low fraud susceptibility as competence gaps typically lead to errors rather than fraud.",
          assessedBy: "James Park",
          assessedAt: new Date("2026-01-22"),
        },
        residualLevel: "low",
        residualScore: {
          components: {complexity: 2, subjectivity: 2, change: 2, uncertainty: 2, fraudSusceptibility: 1},
          rationale:
            "Annual competence assessment (CE-05) provides documented evaluation of key personnel. CPD requirements and succession planning further mitigate the risk. Residual risk is low as current assessment indicates no significant competence gaps.",
          assessedBy: "James Park",
          assessedAt: new Date("2026-01-22"),
        },
        status: "complete",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-ce-05",
            controlNumber: "CE-05",
            title: "Annual Competence Assessment",
            description:
              "The CFO conducts an annual competence assessment of key financial reporting personnel, evaluating technical knowledge, judgment quality, and control awareness. Results inform training plans for the subsequent year.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2026-01-10"),
            status: "complete",
          },
        ],
      },
    ],
  },
  {
    elcId: "elc-monitoring-governance",
    risks: [
      {
        id: "risk-moc-01",
        riskNumber: "MOC-R01",
        title: "Ineffective ongoing monitoring of control performance",
        description:
          "Management may not detect control breakdowns in a timely manner due to inadequate ongoing monitoring activities, allowing control failures to persist and potentially leading to undetected errors in financial reporting.",
        owner: "Lisa Wang",
        inherentScore: {
          components: {complexity: 3, subjectivity: 3, change: 4, uncertainty: 4, fraudSusceptibility: 3},
          rationale:
            "High change risk as new business processes and system changes may not be reflected in monitoring parameters. High uncertainty as control breakdowns may occur between monitoring cycles. Moderate fraud susceptibility as unmonitored controls create opportunity for override.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-20"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 2, change: 3, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "Monthly KPI dashboard review (MOC-01) provides regular visibility into control performance trends. Automated exception alerting (MOC-02) provides real-time detection for predefined conditions. Residual change and uncertainty persist as monitoring rules may not cover all emerging risk scenarios.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-20"),
        },
        status: "in-progress",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-moc-01",
            controlNumber: "MOC-01",
            title: "Monthly KPI Dashboard Review",
            description:
              "The Controller reviews a monthly dashboard of key performance indicators covering transaction processing accuracy, reconciliation completion rates, exception volumes, and control execution timeliness. Metrics trending outside established thresholds trigger investigation and escalation.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2026-01-15"),
            status: "complete",
          },
          {
            id: "ctrl-moc-02",
            controlNumber: "MOC-02",
            title: "Automated Exception Alerting",
            description:
              "The ERP system generates real-time alerts for predefined exception conditions including duplicate payments, unusual journal entries, segregation-of-duties violations, and transactions exceeding approval limits. Alerts are routed to the relevant control owner for investigation within 24 hours.",
            designScore: 3,
            implementationScore: 2,
            lastAssessed: new Date("2026-01-15"),
            status: "in-review",
          },
        ],
      },
      {
        id: "risk-moc-02",
        riskNumber: "MOC-R02",
        title: "Ineffective deficiency remediation",
        description:
          "Identified control deficiencies may not be remediated in a timely manner, allowing weaknesses to persist and potentially leading to material misstatement or regulatory findings.",
        owner: "James Park",
        inherentScore: {
          components: {complexity: 3, subjectivity: 3, change: 4, uncertainty: 4, fraudSusceptibility: 2},
          rationale:
            "High change risk as remediation timelines are often impacted by competing organisational priorities. High uncertainty as the effectiveness of remediation cannot always be confirmed without re-testing.",
          assessedBy: "James Park",
          assessedAt: new Date("2026-01-22"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 2, change: 3, uncertainty: 3, fraudSusceptibility: 1},
          rationale:
            "Deficiency tracking with SLA monitoring (MOC-03) reduces subjectivity and provides transparency. Remediation re-testing (MOC-04) addresses uncertainty by confirming fix effectiveness. Change risk persists as organisational priorities still impact timelines.",
          assessedBy: "James Park",
          assessedAt: new Date("2026-01-22"),
        },
        status: "in-progress",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-moc-03",
            controlNumber: "MOC-03",
            title: "Deficiency Tracking and SLA Monitoring",
            description:
              "All identified deficiencies are logged in a centralised tracker with severity-based SLAs. Management reviews the tracker monthly and reports overdue items to the audit committee.",
            designScore: 2,
            implementationScore: 2,
            lastAssessed: new Date("2026-01-10"),
            status: "in-progress",
          },
          {
            id: "ctrl-moc-04",
            controlNumber: "MOC-04",
            title: "Remediation Re-Testing",
            description:
              "Internal audit performs re-testing of remediated deficiencies rated moderate or above before closure to confirm the control weakness has been adequately addressed.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2026-01-10"),
            status: "complete",
          },
        ],
      },
      {
        id: "risk-moc-03",
        riskNumber: "MOC-R03",
        title: "Inadequate monitoring of outsourced service providers",
        description:
          "The organisation may not adequately monitor outsourced service providers, resulting in undetected control failures at third parties that process or store financially significant data.",
        owner: "Robert Martinez",
        inherentScore: {
          components: {complexity: 4, subjectivity: 3, change: 3, uncertainty: 5, fraudSusceptibility: 3},
          rationale:
            "Very high uncertainty as the organisation has limited direct visibility into third-party control environments. High complexity from multiple service providers with different control frameworks.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-20"),
        },
        residualLevel: "significant",
        residualScore: {
          components: {complexity: 3, subjectivity: 2, change: 2, uncertainty: 4, fraudSusceptibility: 2},
          rationale:
            "Annual SOC report review (MOC-05) provides assurance over service provider controls but only on an annual cycle. Interim monitoring (MOC-06) partially addresses the gap between annual reviews. Residual uncertainty remains high as point-in-time assessments may not capture interim control failures.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-20"),
        },
        status: "in-review",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-moc-05",
            controlNumber: "MOC-05",
            title: "Annual SOC Report Review",
            description:
              "Management obtains and reviews SOC 1 Type II reports for all outsourced service providers that process financially significant data, evaluating complementary user entity controls and noting any qualified opinions.",
            designScore: 3,
            implementationScore: 2,
            lastAssessed: new Date("2025-12-20"),
            status: "in-review",
          },
          {
            id: "ctrl-moc-06",
            controlNumber: "MOC-06",
            title: "Interim Service Provider Monitoring",
            description:
              "For critical outsourced service providers, management performs interim monitoring activities between annual SOC report periods, including quarterly service performance reviews and incident reporting assessments.",
            designScore: 2,
            implementationScore: 2,
            lastAssessed: new Date("2025-12-20"),
            status: "in-progress",
          },
        ],
      },
    ],
  },
  {
    elcId: "elc-risk-assessment",
    risks: [
      {
        id: "risk-ra-01",
        riskNumber: "RA-R01",
        title: "Incomplete identification of business risks relevant to financial reporting",
        description:
          "The entity's risk identification process may not capture all business risks that have a bearing on financial reporting, leading to unaddressed risks that could result in material misstatement.",
        owner: "Lisa Wang",
        inherentScore: {
          components: {complexity: 3, subjectivity: 4, change: 4, uncertainty: 4, fraudSusceptibility: 2},
          rationale:
            "High subjectivity in determining which business risks are relevant to financial reporting. High change risk as new risks emerge with evolving business environment. Low fraud susceptibility as incomplete identification is more likely an oversight than intentional.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-20"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 3, change: 3, uncertainty: 3, fraudSusceptibility: 1},
          rationale:
            "Formal risk register with quarterly updates (RA-01) reduces complexity and provides structure. Risk champion network (RA-02) brings diverse perspectives. Residual change and uncertainty persist as reactive identification of operational changes leaves gaps.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-20"),
        },
        status: "in-progress",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-ra-01",
            controlNumber: "RA-01",
            title: "Quarterly Risk Register Review",
            description:
              "The risk committee conducts a quarterly review of the enterprise risk register, assessing completeness of risk identification and relevance to financial reporting objectives.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2026-01-15"),
            status: "complete",
          },
          {
            id: "ctrl-ra-02",
            controlNumber: "RA-02",
            title: "Business Unit Risk Champion Network",
            description:
              "Designated risk champions in each business unit are responsible for identifying and escalating emerging risks to the central risk management function on an ongoing basis.",
            designScore: 3,
            implementationScore: 2,
            lastAssessed: new Date("2026-01-15"),
            status: "in-review",
          },
        ],
      },
      {
        id: "risk-ra-02",
        riskNumber: "RA-R02",
        title: "Inadequate responsiveness to significant changes",
        description:
          "The entity may not identify, assess, or respond to significant changes (new products, system migrations, regulatory developments, organisational changes) in a timely manner, leaving financial reporting controls misaligned with current operations.",
        owner: "James Park",
        inherentScore: {
          components: {complexity: 4, subjectivity: 3, change: 5, uncertainty: 4, fraudSusceptibility: 2},
          rationale:
            "Very high change risk as the entity has experienced multiple significant changes in the current period. High complexity from the diverse nature of changes requiring different assessment approaches.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-22"),
        },
        residualLevel: "significant",
        residualScore: {
          components: {complexity: 3, subjectivity: 2, change: 4, uncertainty: 4, fraudSusceptibility: 1},
          rationale:
            "The change impact assessment process (RA-03) provides a structured template but is not consistently applied. Regulatory changes are well-tracked but operational and market changes are reactive. High residual risk given that 3 of 5 recent significant changes were not fully addressed.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2026-01-22"),
        },
        status: "in-progress",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-ra-03",
            controlNumber: "RA-03",
            title: "Change Impact Assessment Process",
            description:
              "Business units are required to complete a change impact assessment template for significant changes, evaluating financial reporting implications and required control modifications.",
            designScore: 3,
            implementationScore: 2,
            lastAssessed: new Date("2026-01-10"),
            status: "in-progress",
          },
        ],
      },
      {
        id: "risk-ra-03",
        riskNumber: "RA-R03",
        title: "Weak linkage between identified risks and control responses",
        description:
          "The entity's risk register may not be effectively linked to specific control activities, making it difficult to assess whether identified risks are adequately mitigated and to identify control gaps.",
        owner: "Lisa Wang",
        inherentScore: {
          components: {complexity: 3, subjectivity: 4, change: 3, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "High subjectivity in determining whether control responses adequately address identified risks. Moderate complexity from the need to map risks across multiple processes and control layers.",
          assessedBy: "Lisa Wang",
          assessedAt: new Date("2026-01-25"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 3, change: 2, uncertainty: 2, fraudSusceptibility: 1},
          rationale:
            "The risk-to-control mapping exercise (RA-04) provides formal documentation of linkages. Residual subjectivity remains as the adequacy of control responses involves professional judgement.",
          assessedBy: "Lisa Wang",
          assessedAt: new Date("2026-01-25"),
        },
        status: "in-review",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-ra-04",
            controlNumber: "RA-04",
            title: "Risk-to-Control Mapping Exercise",
            description:
              "The risk management function maintains a formal mapping of identified business risks to specific control activities, reviewed annually to ensure completeness and relevance.",
            designScore: 2,
            implementationScore: 2,
            lastAssessed: new Date("2026-01-08"),
            status: "in-progress",
          },
        ],
      },
    ],
  },
  {
    elcId: "elc-it-environment",
    risks: [
      {
        id: "risk-gitc-01",
        riskNumber: "GITC-R01",
        title: "Unauthorized user access provisioning",
        description:
          "User accounts may be created or provisioned without appropriate approval, granting unauthorized individuals access to financially significant applications and data.",
        owner: "David Kim",
        inherentScore: {
          components: {complexity: 4, subjectivity: 3, change: 5, uncertainty: 4, fraudSusceptibility: 4},
          rationale:
            "Very high inherent change risk from the ongoing cloud migration affecting identity management infrastructure. High complexity from multiple provisioning pathways across legacy and cloud systems. Significant fraud susceptibility from privileged access creation.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-05"),
        },
        residualLevel: "significant",
        residualScore: {
          components: {complexity: 3, subjectivity: 2, change: 4, uncertainty: 3, fraudSusceptibility: 3},
          rationale:
            "The access request approval workflow (GITC-01) reduces subjectivity by requiring documented manager and system owner sign-off. Quarterly access reviews (GITC-02) provide detective coverage but partial implementation effectiveness limits their impact. Change risk remains high as the cloud migration introduces new provisioning pathways faster than controls can be adapted.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-05"),
        },
        status: "in-review",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-gitc-01a",
            controlNumber: "GITC-01",
            title: "Access Request Approval Workflow",
            description:
              "All access requests require documented approval from the user's manager and system owner before provisioning in the identity management system.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-20"),
            status: "complete",
          },
          {
            id: "ctrl-gitc-01b",
            controlNumber: "GITC-02",
            title: "Quarterly User Access Review",
            description:
              "System owners perform quarterly review of all user access rights, certifying continued need and appropriate role assignment.",
            designScore: 3,
            implementationScore: 2,
            lastAssessed: new Date("2025-10-15"),
            status: "in-review",
          },
        ],
      },
      {
        id: "risk-gitc-02",
        riskNumber: "GITC-R02",
        title: "Untested changes deployed to production",
        description:
          "Application or infrastructure changes may be promoted to production without adequate testing and approval, introducing errors that compromise data integrity or system availability.",
        owner: "Emily Zhang",
        inherentScore: {
          components: {complexity: 4, subjectivity: 3, change: 4, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "High inherent complexity from the multi-tier change management pipeline across application layers. Increased deployment frequency under agile methodology raises change risk. Subjectivity in determining adequate test coverage for each change.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-05"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 3, subjectivity: 2, change: 3, uncertainty: 2, fraudSusceptibility: 1},
          rationale:
            "CAB approval with documented test evidence (GITC-03) and segregation of development/production (GITC-04) together provide strong preventive controls that virtually eliminate fraud susceptibility and reduce subjectivity. Complexity and change risk persist at significant levels due to the inherent nature of multi-tier deployments and agile release cadence.",
          assessedBy: "Platformed AI",
          assessedAt: new Date("2025-12-05"),
        },
        status: "in-review",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-gitc-02a",
            controlNumber: "GITC-03",
            title: "Change Advisory Board Approval",
            description:
              "All production changes require documented approval from the Change Advisory Board, including evidence of testing in a non-production environment.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-18"),
            status: "complete",
          },
          {
            id: "ctrl-gitc-02b",
            controlNumber: "GITC-04",
            title: "Segregation of Development and Production",
            description:
              "Developers do not have direct access to deploy to production; all deployments are executed by a separate release management team.",
            designScore: 3,
            implementationScore: 3,
            lastAssessed: new Date("2025-11-18"),
            status: "complete",
          },
        ],
      },
      {
        id: "risk-gitc-03",
        riskNumber: "GITC-R03",
        title: "Excessive privileged access",
        description:
          "Administrator and privileged accounts may be assigned without adequate justification or monitoring, increasing the risk of unauthorized data modification or system configuration changes.",
        owner: "David Kim",
        inherentScore: {
          components: {complexity: 3, subjectivity: 3, change: 3, uncertainty: 4, fraudSusceptibility: 4},
          rationale:
            "High inherent fraud susceptibility from the power of privileged accounts to bypass application-level controls. High uncertainty as privileged activity spans across multiple systems with varying log completeness.",
          assessedBy: "David Kim",
          assessedAt: new Date("2025-12-04"),
        },
        residualLevel: "medium",
        residualScore: {
          components: {complexity: 2, subjectivity: 2, change: 2, uncertainty: 3, fraudSusceptibility: 2},
          rationale:
            "Monthly privileged activity monitoring (GITC-05) provides detective coverage that reduces fraud susceptibility and subjectivity. However, the partially effective design and implementation mean log review may miss anomalous activity in high-volume data, leaving residual uncertainty elevated.",
          assessedBy: "David Kim",
          assessedAt: new Date("2025-12-04"),
        },
        status: "in-progress",
        cotabdAssertions: [],
        controls: [
          {
            id: "ctrl-gitc-03a",
            controlNumber: "GITC-05",
            title: "Privileged Access Monitoring",
            description:
              "All privileged account activity is logged and reviewed monthly by IT security; anomalous activity triggers investigation within 24 hours.",
            designScore: 2,
            implementationScore: 2,
            lastAssessed: new Date("2025-11-01"),
            status: "in-progress",
          },
        ],
      },
    ],
  },
];
